/**
 * FeaturedCompletions — reusable component that surfaces recently
 * shipped, publicly approved work for a single service (or the whole
 * ecosystem) from the LIVE federation API.
 *
 * Source of truth: `useEcosystemDataset(lang)` →
 * `RELEASES[]` filtered by `serviceId` if specified. Sorted
 * newest-first by the adapter already.
 *
 * Owner rules respected:
 *   - Only API-derived completions render. No fabrication, no
 *     placeholder cards (we return null instead of inventing).
 *   - publicDisplayApproved filtering already enforced upstream.
 *   - Each card deep-links to /:lang/whats-new?release=vX.Y.Z so the
 *     visitor can see the full release detail.
 *
 * Used at three densities per the owner's directive:
 *   - HomePage   → limit=2  (compact preview inside each service panel)
 *   - Services   → limit=3  (richer per-service highlight band)
 *   - Service detail pages → limit=6  (deferred — not built yet)
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

import { useEcosystemDataset } from "@/lib/useEcosystemData";
import { formatPublicDate } from "@/lib/formatPublicDate";

const SERVICE_HEX = {
  japanetccard: "#5B2E83",
  japantollreceipts: "#2F8F3C",
  japanchangemoney: "#0E5FB0",
  "nojo-enterprise": "#0F1D2D",
};

const SERVICE_LABELS = {
  japanetccard: "JapanETCcard.com",
  japantollreceipts: "JapanTollReceipts.jp",
  japanchangemoney: "JapanChangeMoney.com",
  "nojo-enterprise": "NoJo Enterprise",
};

function pickLang(field, lang) {
  if (!field) return "";
  if (typeof field === "string") return field;
  return field[lang] || field.en || field.ja || "";
}

export default function FeaturedCompletions({
  lang,
  serviceId = null,
  limit = 3,
  compact = false,
  heading = true,
  testIdPrefix,
}) {
  const dataset = useEcosystemDataset(lang);
  const isJa = lang === "ja";

  const completions = (dataset.RELEASES || [])
    .filter((r) => (serviceId ? r._serviceId === serviceId : true))
    .slice(0, limit);

  if (!dataset.loaded) return null;
  if (completions.length === 0) return null;

  const accent = serviceId ? SERVICE_HEX[serviceId] : SERVICE_HEX["nojo-enterprise"];
  const title = isJa ? "最近の成果" : "Featured completions";
  const subtitle = isJa
    ? serviceId
      ? `${SERVICE_LABELS[serviceId] || serviceId}の最新リリース`
      : "ライブAPIから取得した、最近の公開更新"
    : serviceId
    ? `Recent shipped work from ${SERVICE_LABELS[serviceId] || serviceId}`
    : "Recent approved updates from across the ecosystem";

  const prefix = testIdPrefix || `featured-${serviceId || "all"}`;

  return (
    <section
      className={compact ? "" : "py-6"}
      data-testid={prefix}
      aria-label={isJa ? "最近の成果" : "Featured completions"}
    >
      {heading && (
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
          <div>
            <div
              className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold inline-flex items-center gap-1.5"
              style={{ color: accent }}
            >
              <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
              {title}
            </div>
            <p className="text-[12.5px] text-slate-500 mt-1">{subtitle}</p>
          </div>
          <Link
            to={`/${lang}/whats-new${serviceId ? "" : ""}`}
            data-testid={`${prefix}-viewall`}
            className="text-[11px] tracking-[0.18em] font-mono-display uppercase font-bold text-slate-500 hover:text-[#0F1D2D] transition-colors inline-flex items-center gap-1"
          >
            {isJa ? "すべて見る" : "View all"}
            <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
          </Link>
        </div>
      )}

      <ul
        className={`grid gap-3 ${
          limit <= 2
            ? "grid-cols-1 sm:grid-cols-2"
            : limit <= 3
            ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        {completions.map((r) => {
          const hex = SERVICE_HEX[r._serviceId] || accent;
          const label = SERVICE_LABELS[r._serviceId] || r._serviceId;
          const dateStr = formatPublicDate(r.sortDate || r.date, lang);
          return (
            <li key={r.version} data-testid={`${prefix}-card-${r.version}`}>
              <Link
                to={`/${lang}/whats-new?release=${encodeURIComponent(r.version)}`}
                className="group block h-full rounded-xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:shadow-sm transition-all overflow-hidden"
              >
                <span aria-hidden className="block h-0.5 w-full" style={{ backgroundColor: hex }} />
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono-display font-bold tracking-tight"
                      style={{ color: hex, backgroundColor: `${hex}10` }}
                    >
                      {r.version}
                    </span>
                    {!serviceId && (
                      <span
                        className="text-[9.5px] tracking-[0.14em] font-mono-display uppercase font-semibold"
                        style={{ color: hex }}
                      >
                        {label}
                      </span>
                    )}
                  </div>
                  <h3
                    className="text-[13.5px] font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#0F1D2D]"
                    title={pickLang(r.title, lang)}
                  >
                    {pickLang(r.title, lang)}
                  </h3>
                  {pickLang(r.desc, lang) && (
                    <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed line-clamp-2">
                      {pickLang(r.desc, lang)}
                    </p>
                  )}
                  <div className="mt-3 flex items-center justify-between text-[10.5px] text-slate-400">
                    <span className="font-mono">{dateStr}</span>
                    <ArrowUpRight
                      className="h-3 w-3 text-slate-300 group-hover:text-slate-700 transition-colors"
                      strokeWidth={2}
                    />
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
